import * as React from 'react';
import {useEffect, useState} from 'react';
import {Navigate, Route, Routes, useLocation} from 'react-router-dom';
import Layout from './components/Layout';
import PaymentTransaction from "./containers/PaymentTransaction/Loadable";
import {useAuth0} from "./Auth";
import PrivateRoute from "./components/PrivateRoute";
import Login from "./components/Login";
import {CircularProgress, createTheme, ThemeProvider} from "@mui/material";
import Homeowners from "./containers/Homeowners/Loadable";
import FaqComponent from "./components/FaqComponent";
import Payouts from './containers/Payouts/Loadable';
import LockboxFile from './containers/LockboxFiles/Loadable';
import Auth0Users from "./containers/Auth0Users/Loadable";
import Onboarding from "./containers/Onboarding/Loadable";
import OnboardingSuccess from "./containers/Onboarding/components/OnboardingSuccess";
import TaxForms from './containers/TaxForms/Loadable';
import RepresentativeProfile from "./containers/RepresentativeProfile/Loadable";
import RepresentativeUsers from "./containers/RepresentativeUsers/Loadable";
import Reports from "./containers/Reports/Loadable";
import {UserInterface} from "./interfaces/user.interface";
import {config} from "./config";
import NotFound from "./components/NotFound";
import PageRemoved from "./components/PageRemoved";
import AuthLoginError from "./components/AuthLoginError";
import AdminPage from "./containers/Admin/Loadable";
import {useAppDispatch} from "./store/hooks";
import {useClaspoWidget} from "./hooks/useClaspo";
import {useAppInit} from "./hooks/useAppInit";
import {hasStoredAuthLoginErrorMessage, isAuthLoginErrorRoute, LOGIN_ERROR_ROUTE} from "./utils/authLoginError";
const theme = createTheme();

const App = () => {
  const location = useLocation();
  const { loading, isAuthenticated, getTokenSilently, user, userProduct } = useAuth0();
  const [userInfo, setUserInfo] = useState<UserInterface>();
  const [ loadingUser, setLoadingUser ] = useState(false);
  const dispatch = useAppDispatch();
  const { loadClaspo } = useClaspoWidget();
  const {userType} = useAppInit({
    isAuthenticated,
    user,
    userProduct,
    userInfo,
    setUserInfo,
    setLoadingUser,
    getTokenSilently,
    dispatch,
    config,
  });
  
  useEffect(() => {
    if (loading || !user || !userType) return;
    // Define routes where Claspo should NOT appear
    const excludedRoutes = ["/Login", "/Admin", "/TaxForms", "/Profile", "/Representative"];
    const showClaspo = !excludedRoutes.includes(location.pathname);
    
    // Load Claspo only when needed
    loadClaspo(showClaspo);
  }, [userType, user, loading, location.pathname, loadClaspo]);

  if (hasStoredAuthLoginErrorMessage() && !isAuthLoginErrorRoute(location.pathname)) {
    return <Navigate to={LOGIN_ERROR_ROUTE} replace />;
  }
  
  
  if (loading || loadingUser) {
    return (
      <ThemeProvider theme={theme}>
        <div className="mt-5">
          <div className="text-center">
            <CircularProgress size={70} disableShrink />
          </div>
        </div>
      </ThemeProvider>
    )
  }
  
  if (userInfo?.globalReportsEnabled) {
    if (userInfo.isReportsUser
      && !userInfo.isOnboardUser
      && !userInfo.isDashboardUser
      && !userInfo.fullAccessProfile
    ) {
      if (userInfo.userCompany?.reportsEnabled) {
        return (
          <Layout>
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/LoginError" element={<AuthLoginError />} />
              
              {/* Replaces Redirects */}
              <Route path="/Home" element={<Navigate to="/Reports" replace />} />
              <Route path="/PaymentTransaction" element={<Navigate to="/Reports" replace />} />
              <Route path="/Payments" element={<Navigate to="/Reports" replace />} />
              
              {/* PrivateRoute logic must be wrapped manually */}
              <Route path="/Reports"
                element={
                  <PrivateRoute>
                    <Reports />
                  </PrivateRoute>
                }
              />
              
              {/* 404 fallback */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Layout>
        );
      } else {
        return (
          <ThemeProvider theme={theme}>
            <Layout>
              <Routes>
                {/* Homepage */}
                <Route path="/" element={<Login />} />
                <Route path="/LoginError" element={<AuthLoginError />} />
                
                {/* Protected route */}
                <Route
                  path="/Reports"
                  element={
                    <PrivateRoute>
                      <PageRemoved />
                    </PrivateRoute>
                  }
                />
                
                {/* Catch-all route */}
                <Route path="*" element={<PageRemoved />} />
              </Routes>
            </Layout>
          </ThemeProvider>
        );
      }
    }
  }
  
  
  if (userInfo?.isOnboardUser
    && !userInfo.isReportsUser
    && !userInfo.isDashboardUser
    && !userInfo.fullAccessProfile
  ) {
    return (
      <ThemeProvider theme={theme}>
        <Layout>
          <Routes>
            {/* Public route */}
            <Route path="/" element={<Login />} />
            <Route path="/LoginError" element={<AuthLoginError />} />
            
            {/* Redirects */}
            <Route path="/Home" element={<Navigate to="/Onboarding" replace />} />
            <Route path="/PaymentTransaction" element={<Navigate to="/Onboarding" replace />} />
            <Route path="/Payments" element={<Navigate to="/Onboarding" replace />} />
            
            {/* Private routes */}
            <Route
              path="/Onboarding"
              element={
                <PrivateRoute>
                  <Onboarding />
                </PrivateRoute>
              }
            />
            <Route
              path="/OnboardingSuccess"
              element={
                <PrivateRoute>
                  <OnboardingSuccess />
                </PrivateRoute>
              }
            />
            <Route
              path="/TaxForms"
              element={
                <PrivateRoute>
                  <TaxForms />
                </PrivateRoute>
              }
            />
            
            {/* 404 fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </ThemeProvider>
    );
  }
  
  return (
    <ThemeProvider theme={theme}>
      <Layout>
        <Routes>
          {/* Public Route */}
          <Route path="/" element={<Login />} />
          <Route path="/LoginError" element={<AuthLoginError />} />
          
          {/* Conditional Redirect for /Home */}
          <Route
            path="/Home"
            element={
              userInfo?.isOnboardUser ? (
                <Navigate to="/Onboarding" replace />
              ) : userInfo?.globalReportsEnabled &&
              userInfo?.isReportsUser &&
              userInfo?.userCompany?.reportsEnabled ? (
                <Navigate to="/Reports" replace />
              ) : (
                <Navigate to="/Payments" replace />
              )
            }
          />
          
          {/* Other redirects */}
          <Route path="/PaymentTransaction" element={<Navigate to="/Payments" replace />} />
          
          {/* Private Routes */}
          <Route
            path="/Payments"
            element={
              <PrivateRoute>
                <PaymentTransaction />
              </PrivateRoute>
            }
          />
          <Route
            path="/Homeowners"
            element={
              <PrivateRoute>
                <Homeowners />
              </PrivateRoute>
            }
          />
          <Route
            path="/FAQ"
            element={
              <PrivateRoute>
                <FaqComponent />
              </PrivateRoute>
            }
          />
          <Route
            path="/Payouts"
            element={
              <PrivateRoute>
                <Payouts />
              </PrivateRoute>
            }
          />
          <Route
            path="/Payments/ScheduleReports"
            element={
              <PrivateRoute>
                <PaymentTransaction />
              </PrivateRoute>
            }
          />
          <Route
            path="/DashboardUser"
            element={
              <PrivateRoute>
                <Auth0Users />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/LockboxFiles"
            element={
              <PrivateRoute>
                <LockboxFile />
              </PrivateRoute>
            }
          />
          
          {/* Conditional Routes */}
          {userInfo?.isFSEmployee || userInfo?.isOnboardUser || userInfo?.fullAccessProfile ? (
            <Route
              path="/Onboarding"
              element={
                <PrivateRoute>
                  <Onboarding />
                </PrivateRoute>
              }
            />
          ) : null}
          
          <Route
            path="/OnboardingSuccess"
            element={
              <PrivateRoute>
                <OnboardingSuccess />
              </PrivateRoute>
            }
          />
          
          <Route
            path="/TaxForms"
            element={
              <PrivateRoute>
                <TaxForms />
              </PrivateRoute>
            }
          />
          
          {userInfo?.isFSEmployee ? (
            <Route
              path="/Representative"
              element={
                <PrivateRoute>
                  <RepresentativeUsers />
                </PrivateRoute>
              }
            />
          ) : null}
          
          {userInfo?.globalReportsEnabled &&
          (userInfo?.isFSEmployee || userInfo?.isReportsUser) ? (
            <Route
              path="/Reports"
              element={
                <PrivateRoute>
                  <Reports />
                </PrivateRoute>
              }
            />
          ) : null}
          
          {/* Public Route (not private) */}
          <Route path="/Profile" element={<RepresentativeProfile />} />
          
          {userInfo?.isFSEmployee ? (
            <Route
              path="/Admin"
              element={
                <PrivateRoute>
                  <AdminPage />
                </PrivateRoute>
              }
            />
          ) : null}
          
          {/* Fallback route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </ThemeProvider>
  )
  
};

export default App
